import * as React from 'react'
import styled from 'theme/styled-components'
import useTheme from 'theme/useTheme'

import useI18n from 'i18n/useI18n'

import api from './api'
import Loader from 'react-loader-spinner'

interface OpenButtonProps {
  lockerId: string
  userType?: UserType
  siteId?: string
  duration?: number // Duration in milliseconds
  disabled?: boolean
}

const OpenButton: React.FC<OpenButtonProps> = ({ lockerId, userType, siteId, duration = 10000, disabled }) => {
  const i18n = useI18n()
  const [Theme] = useTheme()

  const [progress, setProgress] = React.useState(100)
  const [isAnimating, setIsAnimating] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  const animateButton = () => {
    setIsAnimating(true)
    setProgress(100)

    const startTime = Date.now()
    const animate = () => {
      const currentTime = Date.now()
      const elapsed = currentTime - startTime
      const newProgress = Math.max(0, 100 - (elapsed / duration) * 100)

      setProgress(newProgress)

      if (newProgress > 0) {
        requestAnimationFrame(animate)
      } else {
        setIsAnimating(false)
        setTimeout(() => setProgress(100), 100)
      }
    }

    requestAnimationFrame(animate)
  }

  const handleClick = () => {
    if (disabled || isAnimating || !userType || !siteId) return

    setIsLoading(true)
    api
      .openLocker(userType, siteId, lockerId)
      .then(() => animateButton())
      .finally(() => setIsLoading(false))
  }

  return (
    <ButtonContainer onClick={handleClick} clickDisabled={disabled || isAnimating} disabled={disabled}>
      <ProgressBar width={progress} isAnimating={isAnimating} disabled={disabled} />
      <ButtonText disabled={disabled}>
        {isLoading ? (
          <Loader type="ThreeDots" color={Theme.colors.white} height={20} width={20} />
        ) : (
          i18n.t(`screens.locker.${isAnimating ? 'opening' : 'open'}`)
        )}
      </ButtonText>
    </ButtonContainer>
  )
}

const ProgressBar = styled.div<{ width: number; isAnimating: boolean; disabled?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: ${(props) => (props.disabled ? props.theme.colors.mediumDarkGrey : props.theme.colors.blue)};
  transition: width 0.1s linear, background-color 0.3s ease;
  width: ${(props) => props.width}%;
`

const ButtonContainer = styled.button<{ clickDisabled: boolean }>`
  display: flex;
  width: 100%;
  height: 50px;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  background-color: ${(props) => props.theme.colors.mediumDarkGrey};
  cursor: ${(props) => (props.clickDisabled ? 'not-allowed' : 'pointer')};
  overflow: hidden;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
  }
`

const ButtonText = styled.span<{ disabled?: boolean }>`
  ${(props) => (props.disabled ? props.theme.fonts.body : props.theme.fonts.bodyBold)};
  position: relative;
  z-index: 1;
  color: ${(props) => (props.disabled ? props.theme.colors.placeholderGrey : props.theme.colors.white)};
`

export default OpenButton
